import { useState } from 'react';
import { PageHeader } from '@ui-kit';
import type { PermissionDemoTab } from '../types/permissionDemo';
import { MyPermissionsTab } from '../components/MyPermissionsTab';
import { DemoEndpointsTab } from '../components/DemoEndpointsTab';
import { RoleSimulatorTab } from '../components/RoleSimulatorTab';
import { PermissionMatrixTab } from '../components/PermissionMatrixTab';

/** Keyframe CSS — injected once as a style tag */
const KEYFRAME_CSS = `
@keyframes spin  { to { transform: rotate(360deg); } }
@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
`;

interface Tab {
  key: PermissionDemoTab;
  label: string;
}

const TABS: Tab[] = [
  { key: 'myPermissions', label: '내 권한' },
  { key: 'demoEndpoints', label: '데모 endpoint' },
  { key: 'simulator', label: '시뮬레이터' },
  { key: 'matrix', label: '매트릭스' },
];

export function PermissionDemoPage() {
  const [activeTab, setActiveTab] = useState<PermissionDemoTab>('myPermissions');

  return (
    <>
      {/* Inject keyframe animations (no inline hex) */}
      <style>{KEYFRAME_CSS}</style>

      <div
        style={{
          maxWidth: 1080,
          margin: '0 auto',
          padding: 'var(--spacing-6)',
        }}
      >
        {/* Page header */}
        <PageHeader
          title="권한 데모"
          breadcrumb={
            <span>
              개발자도구 / <span style={{ color: 'var(--color-text-primary)' }}>권한 데모</span>
            </span>
          }
          actions={
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
              }}
            >
              RBAC 동작을 실시간으로 확인
            </span>
          }
        />

        {/* Tab navigation */}
        <nav
          role="tablist"
          aria-label="권한 데모 탭"
          style={{
            display: 'flex',
            gap: 'var(--spacing-1)',
            borderBottom: '1px solid var(--color-surface-border)',
            marginBottom: 'var(--spacing-4)',
            overflowX: 'auto',
            marginTop: 'var(--spacing-6)',
          }}
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`tabpanel-${tab.key}`}
                id={`tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setActiveTab(tab.key);
                  }
                }}
                style={{
                  padding: 'var(--spacing-3) var(--spacing-4)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: isActive ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
                  borderBottom: `2px solid ${isActive ? 'var(--color-brand-primary)' : 'transparent'}`,
                  background: 'transparent',
                  border: 'none',
                  borderBottomWidth: 2,
                  borderBottomStyle: 'solid',
                  borderBottomColor: isActive ? 'var(--color-brand-primary)' : 'transparent',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)',
                  fontFamily: 'var(--font-family-sans)',
                  outline: 'none',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* Tab panels */}
        <div
          id={`tabpanel-myPermissions`}
          role="tabpanel"
          aria-labelledby={`tab-myPermissions`}
          hidden={activeTab !== 'myPermissions'}
          tabIndex={0}
        >
          {activeTab === 'myPermissions' && <MyPermissionsTab />}
        </div>

        <div
          id={`tabpanel-demoEndpoints`}
          role="tabpanel"
          aria-labelledby={`tab-demoEndpoints`}
          hidden={activeTab !== 'demoEndpoints'}
          tabIndex={0}
        >
          {activeTab === 'demoEndpoints' && <DemoEndpointsTab />}
        </div>

        <div
          id={`tabpanel-simulator`}
          role="tabpanel"
          aria-labelledby={`tab-simulator`}
          hidden={activeTab !== 'simulator'}
          tabIndex={0}
        >
          {activeTab === 'simulator' && <RoleSimulatorTab />}
        </div>

        <div
          id={`tabpanel-matrix`}
          role="tabpanel"
          aria-labelledby={`tab-matrix`}
          hidden={activeTab !== 'matrix'}
          tabIndex={0}
        >
          {/* PermissionMatrixTab: enabled only when tab is active → lazy fetch */}
          <PermissionMatrixTab enabled={activeTab === 'matrix'} />
        </div>

        {/* Common modules collapsible panel */}
        <CommonUsagePanel />
      </div>
    </>
  );
}

// ─── Common modules panel (collapsible) ─────────────────────────────────────

interface CommonModule {
  module: string;
  api: string;
  role: string;
  snippet: string;
}

const COMMON_MODULES: CommonModule[] = [
  {
    module: 'eraf-security C030',
    api: '@HasPermission("USER_READ")',
    role: '/user-only 메서드 게이트',
    snippet: '@HasPermission("USER_READ") public ApiResponse<...> userOnly() {...}',
  },
  {
    module: 'eraf-security C030',
    api: '@HasAnyPermission({...})',
    role: '/manager-only OR 게이트',
    snippet: '@HasAnyPermission({"USER_WRITE","AUDIT_READ"}) public ApiResponse<...> managerOnly()',
  },
  {
    module: 'eraf-security C030',
    api: 'DynamicPermissionService.permissionsOfRoles()',
    role: '시뮬레이터 결과 계산',
    snippet: 'service.permissionsOfRoles(Set.of("ROLE_USER"))',
  },
  {
    module: 'eraf-security C030',
    api: 'DynamicPermissionService.gatewayCatalog()',
    role: 'matrix 데이터 소스',
    snippet: 'Map<String, Set<String>> catalog = svc.gatewayCatalog();',
  },
  {
    module: 'eraf-security C028',
    api: 'JwtAuthenticationFilter',
    role: '인증 (자동, biz 코드 0줄)',
    snippet: '(filter 자동)',
  },
  {
    module: 'eraf-core C006',
    api: 'RequestContextHolder.current()',
    role: '현재 사용자 추출 (DB 0회)',
    snippet: 'var ctx = RequestContextHolder.current();',
  },
  {
    module: 'eraf-core C002',
    api: 'ApiResponse.success(data)',
    role: '모든 응답 래핑',
    snippet: 'return ApiResponse.success(service.matrix());',
  },
  {
    module: 'eraf-core C001',
    api: 'BusinessException + ErrorCode',
    role: "simulate 의 잘못된 role",
    snippet: 'throw new BusinessException(PERM_DEMO_400_001, role);',
  },
];

function CommonUsagePanel() {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        marginTop: 'var(--spacing-8)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="common-usage-panel"
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-elevated)',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        <span>이 페이지가 사용한 공통 모듈</span>
        <span aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div id="common-usage-panel" style={{ overflowX: 'auto' }}>
          <table
            aria-label="공통 모듈 사용 목록"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-xs)',
              background: 'var(--color-surface-background)',
            }}
          >
            <thead>
              <tr>
                {['모듈', 'API', '역할', '사용 예'].map((h) => (
                  <th
                    key={h}
                    scope="col"
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      textAlign: 'left',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontWeight: 700,
                      background: 'var(--color-surface-muted)',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMMON_MODULES.map((m, idx) => (
                <tr key={idx}>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: idx < COMMON_MODULES.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.module}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: idx < COMMON_MODULES.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                    }}
                  >
                    <code
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        color: 'var(--color-brand-primary)',
                        fontSize: 'var(--font-size-xs)',
                        wordBreak: 'break-all',
                      }}
                    >
                      {m.api}
                    </code>
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: idx < COMMON_MODULES.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {m.role}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: idx < COMMON_MODULES.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                    }}
                  >
                    <code
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        background: 'var(--color-surface-elevated)',
                        padding: '2px var(--spacing-2)',
                        borderRadius: 'var(--radius-sm)',
                        display: 'inline-block',
                        wordBreak: 'break-all',
                      }}
                    >
                      {m.snippet}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
